"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Trust section (T10): sourced data / deterministic validation, memory
 * control, export/delete, and the AI boundary (not a medical service)
 * — ported from main.html plus a 5th card making the AI boundary
 * explicit, since ui-ux-spec.md item 7 calls for it as its own point.
 */

import { CircleCheckBig, Eye, Pencil, ShieldCheck, SlidersHorizontal } from "lucide-react"
import { useI18n } from "@/i18n"
import { Reveal } from "./Reveal"

export function TrustSection() {
  const { t } = useI18n()

  const cards = [
    {
      icon: CircleCheckBig,
      title: t("landingPage.trust.card1Title"),
      body: t("landingPage.trust.card1Body"),
    },
    {
      icon: Eye,
      title: t("landingPage.trust.card2Title"),
      body: t("landingPage.trust.card2Body"),
    },
    {
      icon: Pencil,
      title: t("landingPage.trust.card3Title"),
      body: t("landingPage.trust.card3Body"),
    },
    {
      icon: SlidersHorizontal,
      title: t("landingPage.trust.card4Title"),
      body: t("landingPage.trust.card4Body"),
    },
    {
      icon: ShieldCheck,
      title: t("landingPage.trust.card5Title"),
      body: t("landingPage.trust.card5Body"),
    },
  ]

  return (
    <section id="trust" className="scroll-mt-[12vh] border-b border-line bg-cream">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
        <Reveal>
          <p className="text-sm font-semibold tracking-wide text-green uppercase">
            {t("landingPage.trust.eyebrow")}
          </p>
          <h2 className="mt-2 text-3xl font-bold text-ink md:text-4xl">
            {t("landingPage.trust.heading")}
          </h2>
          <p className="mt-3 max-w-prose text-ink-2">{t("landingPage.trust.lead")}</p>
        </Reveal>

        {/* Owner feedback 2026-08-26 (e): interactive spotlight cards — hover
            lifts the card, sweeps a lime gradient border, and scales the icon
            in a tinted tile. Copy is untouched (legal-reviewed wording). */}
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card, i) => (
            <Reveal key={card.title} as="article" index={(i % 3) + 1} className="group relative">
              <div
                aria-hidden="true"
                className="absolute -inset-px rounded-bb-xl bg-gradient-to-br from-lime/0 via-lime/0 to-lime/0 opacity-0 transition-opacity duration-300 group-hover:from-lime/60 group-hover:via-green/20 group-hover:to-coral/30 group-hover:opacity-100 motion-reduce:transition-none"
              />
              <div className="relative flex h-full flex-col gap-3 rounded-bb-xl border border-line bg-paper p-6 transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-lg group-hover:shadow-green/10 motion-reduce:transition-none motion-reduce:group-hover:translate-y-0">
                <span className="flex size-10 items-center justify-center rounded-bb-lg bg-green/10 transition-transform duration-300 group-hover:scale-110 motion-reduce:transition-none">
                  <card.icon aria-hidden="true" className="size-5 text-green" />
                </span>
                <h3 className="font-display text-lg font-semibold text-ink">{card.title}</h3>
                <p className="text-sm text-ink-2">{card.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}
